import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom"
import { useEffect, createContext } from "react"
import { validateAuthState, syncAuthData, createAuthContext } from "./services/auth"
import "leaflet/dist/leaflet.css"
import "./styles/globalResponsive.css"

// Customer Components
import Landing from "./pages/Landing/view/Landing.jsx"
import InformationForm from "./pages/Register/view/InformationForm.jsx"
import NewMenupage from "./pages/customer/views/NewMenupage.jsx"
import CustomerDashboard from "./pages/customer/Dashboard/views/CustomerDashboard.jsx"
import EventMenu from "./pages/customer/Menu/views/EventMenu.jsx"
import ReviewParchase from "./pages/customer/Purchases/views/ReviewPurchase.jsx"
import EventDetailsForPurchase from "./pages/customer/EventDetailsForPurchase/views/EventDetailsForPurchase.jsx"
import TicketsList from "./pages/customer/Purchases/views/TicketsList.jsx"
import ViewTickets from "./pages/customer/TicketViewing/views/ViewTickets.jsx"
import ConfirmTicketPackage from "./pages/customer/TicketDetails/views/ConfirmTicketPackage.jsx"
import CustomerTicketDetails from "./pages/customer/TicketDetails/views/CustomerTicketDetails.jsx"
import Receipt from "./pages/customer/Payment/views/Receipt.jsx"

// Organiser Components
import OrganiserDash from "./pages/organiser/Dashboard/views/OrganiserDash.jsx"
import RequestCard from "./pages/organiser/EventManagement/views/RequestCard.jsx"
import TicketsRequest from "./pages/organiser/EventManagement/Tickets/views/TicketsRequest.jsx"
import TicketsEventList from "./pages/organiser/EventManagement/Tickets/views/TicketsEventList.jsx"
import EventForm from "./pages/organiser/EventHost/views/EventForm.jsx"
import ViewEventRequest from "./pages/organiser/HostRequest/views/ViewEventRequest.jsx"
import Analytics from "./pages/organiser/Analytics/views/Analytics.jsx"
import RehostEventForm from "./pages/organiser/Rehost/views/RehostEventForm.jsx"
import MyEvents from "./pages/organiser/EventManagement/views/MyEvents.jsx"
import RehostEventDetails from "./pages/organiser/Rehost/views/RehostEventDetails.jsx"
import Attendance from "./pages/organiser/Attendance/views/Attendance.jsx"
import HostRequest from "./pages/organiser/HostRequest/views/HostRequest.jsx"
import OrganiserRevenueDashboard from "./pages/organiser/RevenueDashboard/views/OrganiserRevenueDashboard.jsx"
import OrganiserEventsOverview from "./pages/organiser/EventsOverview/views/OrganiserEventsOverview.jsx"

// Admin Components
import AdminDash from "./pages/admin/AdminDash/views/AdminDash.jsx"
import EventApproval from "./pages/admin/Event/Approval/views/EventApproval.jsx"
import EventsHistory from "./pages/admin/Event/AllEvents/views/EventsHistory.jsx"
import AdminViewEventRequest from "./pages/admin/Event/Approval/views/AdminViewEventRequest.jsx"
import SystemUsers from "./pages/admin/Users/views/SystemUsers.jsx"
import ViewingCustomer from "./pages/admin/Users/views/ViewingCustomer.jsx"
import ViewingOrganiser from "./pages/admin/Users/views/ViewingOrganiser.jsx"
import UserGrowthAnalytics from "./pages/admin/UserGrowthAnalytics/views/UserGrowthAnalytics.jsx"
import EventPerformanceDashboard from "./pages/admin/EventPerformanceDashboard/views/EventPerformanceDashboard.jsx"
import GeographicEventDistribution from "./pages/admin/GeographicEventDistribution/views/GeographicEventDistribution.jsx"
import TicketSalesAnalytics from "./pages/admin/TicketSalesAnalytics/views/TicketSalesAnalytics.jsx"
import OrganiserTicketSalesAnalytics from "./pages/organiser/TicketSalesAnalytics/views/TicketSalesAnalytics.jsx"
import TimeSalesAnalysis from "./pages/organiser/TimeSalesAnalysis/views/TimeSalesAnalysis.jsx"

import ForgotPassword from "./pages/ForgotPassword/ForgotPassword.jsx"
import ErrorBoundary from "./components/ErrorBoundary.jsx"

const ProtectedRoute = ({ children, requireAdmin = false }) => {
  const token = localStorage.getItem('token');
  const userRole = localStorage.getItem('userRole');
  const authContext = createAuthContext();
  
  console.log('ProtectedRoute check:', { 
    token: token ? 'exists' : 'missing', 
    userRole, 
    requireAdmin,
    authContext: {
      isAuthenticated: authContext.isAuthenticated,
      userId: authContext.userId,
      userRole: authContext.userRole
    }
  });
  
  // Enhanced authentication validation
  if (!authContext.isAuthenticated || !token) {
    console.log('No valid authentication found, redirecting to login');
    return <Navigate to="/" replace />;
  }
  
  // Validate authentication state consistency
  if (!validateAuthState()) {
    console.log('Authentication state validation failed, redirecting to login');
    return <Navigate to="/" replace />;
  }
  
  if (requireAdmin) {
    // Case-insensitive admin role check with enhanced validation
    const normalizedRole = authContext.userRole ? authContext.userRole.toLowerCase().trim() : '';
    if (normalizedRole !== 'admin') {
      console.log(`Admin access required, but user role is: ${authContext.userRole} (normalized: ${normalizedRole}), redirecting to home`);
      return <Navigate to="/" replace />;
    }
    console.log('Admin access granted');
  }
  
  // Sync authentication data before rendering protected content
  syncAuthData();
  
  return children;
};

const AuthContext = createContext();

function App() {
  useEffect(() => {
    validateAuthState();
    syncAuthData();
  }, []);

  return (
    <ErrorBoundary>
      <Router
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true,
        }}
      >
        <div style={{ width: "100%", minHeight: "100vh", margin: 0, padding: 0, backgroundColor: "var(--page-background)" }}>
          <Routes>
            {/* Customer Routes */}
            <Route path="/" element={<Landing />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/information-form" element={<InformationForm />} />
            <Route path="/mainmenu" element={
              <ProtectedRoute>
                <NewMenupage />
              </ProtectedRoute>
            } />
            <Route path="/customerdash" element={
              <ProtectedRoute>
                <CustomerDashboard />
              </ProtectedRoute>
            } />
            <Route path="/eventmenu" element={
              <ProtectedRoute>
                <EventMenu />
              </ProtectedRoute>
            } />
            <Route path="/reviewpurchase" element={
              <ProtectedRoute>
                <ReviewParchase />
              </ProtectedRoute>
            } />
            {/* Legacy customer routes removed: parchasedticket, view-more-details, ticketdetails */}
            <Route path="/customerviewevent/:eventId" element={<EventDetailsForPurchase />} />
            <Route path="/customerticketdetails1/:eventId/:packageIndex" element={
              <ProtectedRoute>
                <CustomerTicketDetails />
              </ProtectedRoute>
            } />
            {/* Legacy conference/daypass/invoice/tickets routes removed */}
            <Route path="/ticketslist" element={
              <ProtectedRoute>
                <TicketsList />
              </ProtectedRoute>
            } />
            <Route path="/viewtickets" element={
              <ProtectedRoute>
                <ViewTickets />
              </ProtectedRoute>
            } />
            {/* Legacy viewinvoice/uploadpop routes removed */}
            <Route path="/confirm-ticket-package/:eventId/:packageIndex" element={
              <ProtectedRoute>
                <ConfirmTicketPackage />
              </ProtectedRoute>
            } />
            <Route path="/receipt" element={
              <ProtectedRoute>
                <Receipt />
              </ProtectedRoute>
            } />

            {/* Organiser Routes */}
            <Route path="/organiser-dash" element={
              <ProtectedRoute>
                <OrganiserDash />
              </ProtectedRoute>
            } />
            <Route path="/requestcard" element={
              <ProtectedRoute>
                <RequestCard />
              </ProtectedRoute>
            } />
            <Route path="/event-request" element={
              <ProtectedRoute>
                <HostRequest />
              </ProtectedRoute>
            } />
            <Route path="/ticketsrequest/:eventId" element={
              <ProtectedRoute>
                <TicketsRequest />
              </ProtectedRoute>
            } />
            <Route path="/tickets-event-list" element={
              <ProtectedRoute>
                <TicketsEventList />
              </ProtectedRoute>
            } />
            <Route path="/event-form" element={
              <ProtectedRoute>
                <EventForm />
              </ProtectedRoute>
            } />
            <Route path="/vieweventrequest" element={
              <ProtectedRoute>
                <ViewEventRequest />
              </ProtectedRoute>
            } />
            
            <Route path="/analytics" element={
              <ProtectedRoute>
                <Analytics />
              </ProtectedRoute>
            } />
            
            <Route path="/rehost-event" element={
              <ProtectedRoute>
                <RehostEventForm />
              </ProtectedRoute>
            } />
            <Route path="/my-events" element={
              <ProtectedRoute>
                <MyEvents />
              </ProtectedRoute>
            } />
            <Route path="/view-past-event" element={
              <ProtectedRoute>
                <RehostEventDetails />
              </ProtectedRoute>
            } />
            <Route path="/attendance" element={
              <ProtectedRoute>
                <Attendance />
              </ProtectedRoute>
            } />
            <Route path="/attendance/event/:event_id" element={
              <ProtectedRoute>
                <Attendance />
              </ProtectedRoute>
            } />
            <Route path="/attendance/:event_id/scanner" element={
              <ProtectedRoute>
                <Attendance />
              </ProtectedRoute>
            } />

            {/* Organiser Analytics Routes */}
            <Route path="/organiser-revenue" element={
              <ProtectedRoute>
                <OrganiserRevenueDashboard />
              </ProtectedRoute>
            } />
            <Route path="/organiser-events-overview" element={
              <ProtectedRoute>
                <OrganiserEventsOverview />
              </ProtectedRoute>
            } />
            <Route path="/ticket-sales-analysis" element={
              <ProtectedRoute>
                <OrganiserTicketSalesAnalytics />
              </ProtectedRoute>
            } />
            <Route path="/time-sales-analysis" element={
              <ProtectedRoute>
                <TimeSalesAnalysis />
              </ProtectedRoute>
            } />

            {/* Admin Routes - All Protected and Require Admin Role */}
            <Route 
              path="/admin/view-customer/:id" 
              element={
                <ProtectedRoute requireAdmin={true}>
                  <ViewingCustomer />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/view-organiser/:id" 
              element={
                <ProtectedRoute requireAdmin={true}>
                  <ViewingOrganiser />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/event-approval" 
              element={
                <ProtectedRoute requireAdmin={true}>
                  <EventApproval />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/events-history" 
              element={
                <ProtectedRoute requireAdmin={true}>
                  <EventsHistory />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/adminvieweventrequest" 
              element={
                <ProtectedRoute requireAdmin={true}>
                  <AdminViewEventRequest />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/users" 
              element={
                <ProtectedRoute requireAdmin={true}>
                  <SystemUsers />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/viewingcustomer" 
              element={
                <ProtectedRoute requireAdmin={true}>
                  <ViewingCustomer />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/viewingorganiser" 
              element={
                <ProtectedRoute requireAdmin={true}>
                  <ViewingOrganiser />
                </ProtectedRoute>
              } 
            />

            <Route 
              path="/user-growth-analytics" 
              element={
                <ProtectedRoute requireAdmin={true}>
                  <UserGrowthAnalytics />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/event-performance" 
              element={
                <ProtectedRoute>
                  <EventPerformanceDashboard />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/geographic-events" 
              element={
                <ProtectedRoute>
                  <GeographicEventDistribution />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/ticket-sales" 
              element={
                <ProtectedRoute requireAdmin={true}>
                  <TicketSalesAnalytics />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/*" 
              element={
                <ProtectedRoute requireAdmin={true}>
                  <AdminDash />
                </ProtectedRoute>
              } 
            />
            
            {/* 404 Route - Keep this last */}
          </Routes>
        </div>
      </Router>
    </ErrorBoundary>
  );
}

export default App;
